import React, { useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    Container,
    Divider,
    Grid2 as Grid,
    Stack,
    Typography
} from "@mui/material";
import {
    ArrowForward,
    ConfirmationNumber,
    ExploreOff,
    FitnessCenter,
    Home,
    SupportAgent
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export function NotFound() {
    const navigate = useNavigate();

    useEffect(() => {
        document.title = 'صفحه یافت نشد (۴۰۴) | جیم‌پین';
    }, []);

    const quickLinks = [
        { label: "باشگاه‌ها و مراکز", icon: <FitnessCenter fontSize="small" />, path: "/places" },
        { label: "بلیت‌های فعال", icon: <ConfirmationNumber fontSize="small" />, path: "/tickets" },
        { label: "پشتیبانی آنلاین", icon: <SupportAgent fontSize="small" />, path: "/support" },
    ];

    return (
        <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 6 }, px: 2 }}>
            <Card
                className="animate__animated animate__fadeIn"
                sx={{
                    p: { xs: 3, sm: 4.5 },
                    borderRadius: 4,
                    textAlign: "center",
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(14px)",
                    border: "1px solid rgba(231, 51, 62, 0.12)",
                    boxShadow: "0 12px 36px rgba(0, 0, 0, 0.06)",
                    transition: "all 0.3s ease"
                }}
            >
                {/* Visual Icon Badge */}
                <Box
                    sx={{
                        width: { xs: 84, sm: 96 },
                        height: { xs: 84, sm: 96 },
                        mx: "auto",
                        mb: 2,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "linear-gradient(135deg, rgba(231, 51, 62, 0.12) 0%, rgba(178, 40, 49, 0.04) 100%)",
                        border: "2px solid rgba(231, 51, 62, 0.2)",
                        boxShadow: "0 8px 24px rgba(231, 51, 62, 0.12)",
                    }}
                >
                    <ExploreOff sx={{ fontSize: { xs: 44, sm: 52 }, color: "primary.main" }} />
                </Box>

                {/* Big Gradient 404 Typography */}
                <Typography
                    component="div"
                    sx={{
                        fontSize: { xs: "4.5rem", sm: "6rem" },
                        fontWeight: 900,
                        lineHeight: 1,
                        letterSpacing: "-1.5px",
                        background: "linear-gradient(135deg, #e7333e 0%, #b22831 60%, #ff5252 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        textShadow: "0 10px 24px rgba(231, 51, 62, 0.2)",
                        mb: 0.5,
                        userSelect: "none"
                    }}
                >
                    ۴۰۴
                </Typography>

                {/* Persian Descriptive Texts */}
                <Typography
                    variant="h5"
                    component="h1"
                    sx={{
                        fontWeight: 700,
                        color: "text.primary",
                        mb: 1.2,
                        fontSize: { xs: "1.25rem", sm: "1.5rem" }
                    }}
                >
                    صفحه مورد نظر پیدا نشد!
                </Typography>

                <Typography
                    variant="body2"
                    sx={{
                        color: "text.secondary",
                        lineHeight: 1.85,
                        maxWidth: 400,
                        mx: "auto",
                        mb: 3.5,
                        fontSize: { xs: "0.85rem", sm: "0.92rem" }
                    }}
                >
                    این صفحه منتقل یا حذف شده است.
                </Typography>

                {/* Action Buttons */}
                <Grid container spacing={1.5} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, sm: 7 }}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<Home />}
                            onClick={() => navigate("/")}
                            sx={{
                                py: 1.2,
                                fontWeight: "bold",
                                borderRadius: 3,
                                background: "linear-gradient(135deg, #e7333e 0%, #b22831 100%)",
                                boxShadow: "0 6px 20px rgba(231, 51, 62, 0.35)",
                                "&:hover": {
                                    background: "linear-gradient(135deg, #f14651 0%, #e7333e 100%)",
                                    boxShadow: "0 8px 26px rgba(231, 51, 62, 0.45)"
                                }
                            }}
                        >
                            بازگشت به صفحه اصلی
                        </Button>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 5 }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            size="large"
                            startIcon={<ArrowForward />}
                            onClick={() => navigate(-1)}
                            sx={{
                                py: 1.2,
                                fontWeight: "bold",
                                borderRadius: 3,
                                borderColor: "rgba(0, 0, 0, 0.16)",
                                color: "text.primary",
                                bgcolor: "background.paper",
                                "&:hover": {
                                    borderColor: "primary.main",
                                    color: "primary.main",
                                    bgcolor: "rgba(231, 51, 62, 0.04)"
                                }
                            }}
                        >
                            صفحه قبل
                        </Button>
                    </Grid>
                </Grid>

                <Divider sx={{ my: 2.5, opacity: 0.7 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", px: 1 }}>
                        دسترسی‌های سریع
                    </Typography>
                </Divider>

                {/* Quick Link Shortcuts */}
                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ gap: 1 }}
                >
                    {quickLinks.map((item, idx) => (
                        <Chip
                            key={idx}
                            icon={item.icon}
                            label={item.label}
                            clickable
                            variant="outlined"
                            onClick={() => navigate(item.path)}
                            sx={{
                                py: 2,
                                px: 0.5,
                                borderRadius: 2.5,
                                borderColor: "rgba(0, 0, 0, 0.1)",
                                fontSize: "0.8rem",
                                transition: "all 0.2s ease",
                                "&:hover": {
                                    borderColor: "primary.main",
                                    color: "primary.main",
                                    backgroundColor: "rgba(231, 51, 62, 0.04)"
                                }
                            }}
                        />
                    ))}
                </Stack>
            </Card>
        </Container>
    );
}

export default NotFound;
